import React from 'react'

import {
  AlignItems,
  Box,
  Button,
  ButtonColorVariant,
  ButtonSize,
  ButtonVariant,
  Display,
  DisplayPosition,
  type ResponsiveValue,
  type SpaceScale,
} from '@gousto-internal/citrus-react'

import { useRecipeCarouselNav } from './useRecipeCarouselNav'

type CarouselTrackItem = { id: string }

type CarouselTrackProps<T extends CarouselTrackItem> = {
  items: T[]
  renderItem: (item: T) => React.ReactNode
  ariaLabel: string
  // Vertical placement of the prev/next arrows (horizontal + shape are fixed).
  // Defaults to the recipe carousel's mid-height; Heat & Eat overrides to the image centre.
  navOffset?: React.CSSProperties
  paddingH?: ResponsiveValue<SpaceScale>
}

// Shared Embla scaffold (viewport/container/slides + prev/next arrows) for the
// Gousto Anytime carousels. Owns the nav hook so callers just pass items + renderItem.
export const CarouselTrack = <T extends CarouselTrackItem>({
  items,
  renderItem,
  ariaLabel,
  navOffset = { bottom: '40%' },
  paddingH,
}: CarouselTrackProps<T>) => {
  const { emblaRef, nextBtnDisabled, onNextButtonClick, onPrevButtonClick, prevBtnDisabled } =
    useRecipeCarouselNav()

  return (
    <Box
      role="region"
      aria-roledescription="carousel"
      aria-label={ariaLabel}
      position={DisplayPosition.Relative}
      paddingH={paddingH}
    >
      <Box className="embla__viewport" ref={emblaRef} width="100%" style={{ overflow: 'hidden' }}>
        <Box
          alignItems={AlignItems.Stretch}
          className="embla__container"
          display={Display.Flex}
          gap={3}
          style={{ paddingBottom: '4px' }}
        >
          {items.map((item) => (
            <Box
              key={item.id}
              className="embla__slide"
              display={Display.Flex}
              flexGrow={0}
              flexShrink={0}
            >
              {renderItem(item)}
            </Box>
          ))}
        </Box>
      </Box>

      <Button
        aria-label="Previous slide"
        disabled={prevBtnDisabled}
        colorVariant={ButtonColorVariant.Secondary}
        icon="arrow_back"
        onClick={onPrevButtonClick}
        size={ButtonSize.Large}
        style={{
          borderRadius: '50%',
          display: prevBtnDisabled ? 'none' : 'flex',
          position: 'absolute',
          left: '1%',
          ...navOffset,
        }}
        variant={ButtonVariant.Icon}
      />
      <Button
        aria-label="Next slide"
        disabled={nextBtnDisabled}
        colorVariant={ButtonColorVariant.Secondary}
        icon="arrow_right"
        onClick={onNextButtonClick}
        size={ButtonSize.Large}
        style={{
          borderRadius: '50%',
          display: nextBtnDisabled ? 'none' : 'flex',
          position: 'absolute',
          right: '1%',
          ...navOffset,
        }}
        variant={ButtonVariant.Icon}
      />
    </Box>
  )
}
